// Alta + pago "enterprise" desde una URL personalizada (una por plan, ej.
// `/alta-empresa/ultra-1`). Para clientes ya hablados por ventas: NO pasa por la
// generación de imagen de ejemplo del onboarding normal.
//
// Flujo (columna derecha, en pasos): crear cuenta / iniciar sesión → datos de la
// empresa (mismo form que el onboarding) → pago con Rebill → redirige a la app.
// La columna izquierda (resumen del plan) queda fija, igual que `Checkout.tsx`
// (mismo diseño; responsive: 2 columnas en desktop, stackeado en mobile).
// Ver ADR.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CheckCircle2,
  AlertCircle,
  Loader2,
  Mail,
  Lock,
  ChevronDown,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { fetchStripeCheckoutUrl } from '../lib/paymentServer';
import {
  captureAttributionParams,
  saveSignupAttribution,
} from '../lib/signupAttribution';
import {
  getPlanById,
  ENTERPRISE_PLAN_SLUGS,
  paisFlags,
  paisDesdeCodigoISO,
  getPlanPriceForPais,
  formatPriceForPais,
} from '../lib/checkoutPlans';
import {
  featuresPlanCustom,
  fetchPlanCustom,
  formatPrecioCustom,
  nombrePlanSinPrefijo,
  PlanCustomNoDisponible,
  type PlanCustomPublico,
} from '../lib/planesCustom';
import { CountrySelect, RubroSelect } from '../components/OnboardingSelects';
import { createCompanyForUser } from '../lib/createCompany';
import { isPaidPlan } from '../lib/planCapabilities';
import RebillCardPayment, {
  type RebillCheckoutResult,
} from '../components/RebillCardPayment';

type Step = 'loading' | 'auth' | 'datos' | 'pago' | 'exito' | 'error-estado';

// A dónde va tras pagar (el usuario pidió InterfazV2, sin imagen de ejemplo).
const POST_PAY_DEST = '/interfaz-v2';

// Decide el paso inicial según el estado de la cuenta. `'redirect'` = la cuenta
// YA tiene un plan pago → no mostramos el pago (evita re-cobro si vuelve al link)
// y la mandamos a la app.
//
// FALLA CERRADO (`'error'`): si alguna de las dos consultas falla, NO se sigue al
// pago. `.maybeSingle()` no tira —devuelve `{ data: null, error }`— así que sin
// mirar el `error` un fallo de red o de RLS se leía como "cuenta nueva sin plan"
// y habilitaba el cobro: para un cliente de Stripe con suscripción activa eso es
// una SEGUNDA suscripción, con la vieja todavía cobrando (ADR-661). El mismo
// fallo en la consulta de `profiles` mandaba a cargar los datos de la empresa de
// nuevo, con riesgo de duplicarla.
async function resolveStepForUser(
  userId: string,
): Promise<'datos' | 'pago' | 'redirect' | 'error'> {
  const { data: profile, error: errorProfile } = await supabase
    .from('profiles')
    .select('id_company')
    .eq('id', userId)
    .maybeSingle();
  if (errorProfile) return 'error';
  const companyId = (profile as { id_company?: string | null } | null)?.id_company;
  if (!companyId) return 'datos';
  const { data: company, error: errorEmpresa } = await supabase
    .from('company_profiles')
    .select('plan')
    .eq('id_company', companyId)
    .maybeSingle();
  // `!company` además del error: `maybeSingle()` devuelve `data: null` sin error
  // cuando no hay fila, y ahí tampoco se sabe si la cuenta tiene plan.
  if (errorEmpresa || !company) return 'error';
  if (isPaidPlan((company as { plan?: string | null } | null)?.plan)) return 'redirect';
  return 'pago';
}

export const EnterpriseCheckout = () => {
  const navigate = useNavigate();
  const { plan: planSlug } = useParams<{ plan: string }>();

  const planId = planSlug ? ENTERPRISE_PLAN_SLUGS[planSlug] : undefined;
  const planCatalogo = planId ? getPlanById(planId) : undefined;

  // Plan a medida (ADR-662): si el slug no es uno de los públicos, puede ser un plan negociado
  // con un cliente puntual. Se le pregunta al payment server, que resuelve nombre + cuota de
  // `planes_custom` y el precio del Price de Stripe en vivo.
  const [planCustom, setPlanCustom] = useState<PlanCustomPublico | null>(null);
  // Arranca cargando SOLO si hace falta consultar: un slug del catálogo se resuelve sincrónico
  // y no debe mostrar spinner ni esperar a la red.
  const [planCustomEstado, setPlanCustomEstado] = useState<
    'no-aplica' | 'cargando' | 'ok' | 'not-found' | 'error'
  >(planCatalogo || !planSlug ? 'no-aplica' : 'cargando');

  useEffect(() => {
    if (planCatalogo || !planSlug) return;
    let cancelado = false;
    (async () => {
      try {
        const p = await fetchPlanCustom(planSlug);
        if (cancelado) return;
        setPlanCustom(p);
        setPlanCustomEstado('ok');
      } catch (err) {
        if (cancelado) return;
        // Un link válido con Stripe caído NO debe decir "link inválido": son mensajes
        // distintos porque las acciones del cliente son distintas (reintentar vs avisarnos).
        setPlanCustomEstado(
          err instanceof PlanCustomNoDisponible && err.tipo === 'not_found'
            ? 'not-found'
            : 'error',
        );
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [planSlug, planCatalogo]);

  const [step, setStep] = useState<Step>('loading');
  const [error, setError] = useState<string | null>(null);
  // OJO: este useState vivía más abajo, después de los `return` tempranos de "plan inválido".
  // Funcionaba de casualidad porque aquel guard era estable durante todo el montaje. El guard
  // de "cargando plan a medida" NO lo es (pasa a 'ok' en el mismo montaje), así que dejarlo
  // abajo rompía las reglas de hooks: React vería más hooks en el segundo render y crashearía.
  const [stripeLoading, setStripeLoading] = useState(false);
  // "Ver qué incluye" (resumen compacto colapsable del plan, en mobile).
  const [showFeatures, setShowFeatures] = useState(false);
  // Cupón "Hot Sale" (el MISMO que el checkout): descuento del primer mes que se
  // auto-aplica y se pasa a Rebill, para que el precio de /alta-empresa matchee
  // el de la landing (evita el mismatch "el primer mes" vs precio full).
  const [coupon, setCoupon] = useState<{
    codigo: string;
    multiplicador: number;
  } | null>(null);

  // ── Auth ──
  const [authMode, setAuthMode] = useState<'signup' | 'signin'>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  // ── Datos de la empresa ──
  const [name, setName] = useState('');
  // País: se inicializa con lo que manda la landing en la URL
  // (`?pais=CL`) para poder mostrar el precio en la moneda correcta desde el
  // PRIMER render y dejar el select ya elegido. Si no viene o es un código
  // desconocido, queda vacío: el usuario lo elige y el precio se muestra en
  // USD hasta entonces, que es el fallback correcto. Ver ADR-592.
  const [pais, setPais] = useState(
    () =>
      paisDesdeCodigoISO(
        new URLSearchParams(window.location.search).get('pais'),
      ) ?? '',
  );
  const [companyName, setCompanyName] = useState('');
  const [industryOption, setIndustryOption] = useState('Moda e Indumentaria');
  const [customIndustry, setCustomIndustry] = useState('');
  const [datosLoading, setDatosLoading] = useState(false);

  // Al montar: si ya hay sesión, arrancar en el paso que corresponda.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!user) {
        setStep('auth');
        return;
      }
      const next = await resolveStepForUser(user.id);
      if (cancelled) return;
      if (next === 'redirect') {
        navigate(POST_PAY_DEST, { replace: true });
        return;
      }
      setStep(next === 'error' ? 'error-estado' : next);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // Tracking del embudo (app-side, best-effort) en `alta_empresa_clicks`. Taxonomía
  // del PDF de tracking (jul-2026). Cada evento lleva el `session_id` + UTMs que
  // mandó la landing en la URL → cierra el linkeo anónimo→pago (antes se contaba
  // sin linkear, ADR-593/594):
  //  · 'plan_click'    → llegada al link (todos, antes de elegir signup/signin).
  //  · 'signup'        → el usuario SE REGISTRA (solo signup, NO signin).
  //  · 'checkout_view'    → ENTRÓ al paso de pago (vio el form, NO que pagó).
  //  · 'checkout_created' → se creó la Checkout Session de Stripe (solo EXTERIOR,
  //                         justo antes del redirect). AR/Rebill no lo emite (in-app).
  //  · 'pago_ok'          → cobró (client-side, SOLO Argentina/Rebill). TRANSITORIO: el
  //                      `payment_confirmed` autoritativo (todos los proveedores) lo
  //                      va a escribir el server de pagos server-side (pendiente).
  // `source` = ?src (pricing/directo). Ver ADR-593 y ADR-594.
  const trackEvent = useCallback(
    (
      event:
        | 'plan_click'
        | 'signup'
        | 'checkout_view'
        | 'checkout_created'
        | 'pago_ok',
    ) => {
      if (!planSlug) return;
      const params = new URLSearchParams(window.location.search);
      const source = params.get('src') || 'directo';
      // Atribución que mandó la landing (?sid, ?utm_*, ?referrer): cierra el linkeo
      // anónimo→pago. La landing genera el session_id y lo pasa en el redirect (no
      // escribe en la base); acá lo persistimos en CADA evento del embudo.
      const sessionId = params.get('sid');
      const utmSource = params.get('utm_source');
      const utmCampaign = params.get('utm_campaign');
      const utmContent = params.get('utm_content');
      const referrer = params.get('referrer');
      // Separa el testeo del tráfico real (localhost/preview, o ?test=1 en prod).
      const isTest =
        /localhost|127\.0\.0\.1|\.pages\.dev|\.vercel\.app/i.test(
          window.location.hostname,
        ) || params.get('test') === '1';
      // Devuelve la promesa del insert (no la desacopla) para que un caller que
      // esté por navegar cross-origin (redirect a Stripe) pueda ESPERARLA y no
      // perder el evento. Los demás call sites la ignoran con `void`.
      return (async () => {
        try {
          // `id_company` se resuelve en el momento del evento, no se cachea. Queda
          // en null en 'plan_click' Y en 'signup' (en plan_click no hay sesión; en
          // signup la hay pero la empresa se crea DESPUÉS, en `handleDatos`); se
          // puebla recién desde 'checkout_view'. No se rellena a posteriori: la RLS
          // es solo INSERT. Antes esto importaba porque se contaba SIN linkear
          // (ADR-593/594); ahora el `session_id` linkea el embudo entero, así que un
          // evento sin id_company igual se ata al pago por su session_id.
          let idCompany: string | null = null;
          try {
            const {
              data: { user },
            } = await supabase.auth.getUser();
            if (user) {
              const { data } = await supabase
                .from('profiles')
                .select('id_company')
                .eq('id', user.id)
                .single();
              idCompany = ((data as any)?.id_company as string) ?? null;
            }
          } catch {
            /* sin sesión o sin perfil todavía → queda null */
          }
          await supabase.from('alta_empresa_clicks').insert({
            plan: planSlug,
            source,
            event,
            id_company: idCompany,
            session_id: sessionId,
            utm_source: utmSource,
            utm_campaign: utmCampaign,
            utm_content: utmContent,
            referrer,
            user_agent: navigator.userAgent,
            is_test: isTest,
          });
        } catch {
          /* best-effort, no bloquea el alta */
        }
      })();
    },
    [planSlug],
  );

  // Guards con `ref` para no duplicar (StrictMode invoca effects 2× en dev;
  // también cubre re-montajes / re-submits).
  const clickTrackedRef = useRef(false);
  const conversionTrackedRef = useRef(false);

  useEffect(() => {
    if (!planId || !planSlug || clickTrackedRef.current) return;
    clickTrackedRef.current = true;
    void trackEvent('plan_click');
  }, [planId, planSlug, trackEvent]);

  // 'conversion' = ENTRÓ al paso de pago. OJO: no significa que pagó — para eso
  // está `pago_ok`.
  //
  // Va atado al ESTADO y no al camino, a propósito. Antes se emitía dentro de
  // `handleDatos`, que es solo UNO de los tres caminos que llegan a 'pago':
  //
  //   1. handleDatos           → cuenta nueva, o cuenta sin empresa
  //   2. signIn                → `resolveStepForUser` devuelve 'pago' directo
  //   3. el effect de montaje  → vuelve con sesión abierta y empresa ya creada
  //
  // Los caminos 2 y 3 son justamente los del cliente que YA tenía cuenta con
  // empresa, que nunca pasa por `handleDatos` y por lo tanto no registraba
  // conversión. Sacar el gate de `didSignupRef` no alcanzaba: el problema no era
  // el gate sino el lugar. Ver ADR-594.
  useEffect(() => {
    if (step !== 'pago' || conversionTrackedRef.current) return;
    conversionTrackedRef.current = true;
    void trackEvent('checkout_view');
  }, [step, trackEvent]);

  // Auto-aplicar el cupón "Hot Sale" (descuento del primer mes), igual que el
  // checkout: mismo cupón por ID; si está activo/vigente y no agotado, se aplica
  // y se pasa a Rebill. Así el precio matchea la landing.
  useEffect(() => {
    // Descuento opcional: si la URL trae `?desc=0`, NO se aplica el cupón →
    // precio real. Sirve para que ventas mande un link a precio full cuando
    // quiera (ej. `alta-empresa/ultra-1?desc=0`). Sin el param → con descuento.
    if (new URLSearchParams(window.location.search).get('desc') === '0') return;
    let cancelled = false;
    (async () => {
      try {
        // RPC security-definer: valida vigencia server-side y devuelve SOLO el
        // cupón del alta (no expone la tabla `cupones` al usuario anónimo, que es
        // quien está en el paso de crear cuenta). Ver SQL en el doc/ADR.
        const { data } = await supabase.rpc('get_alta_empresa_coupon');
        const row = Array.isArray(data) ? data[0] : data;
        if (cancelled || !row) return;
        setCoupon({
          codigo: row.codigo,
          multiplicador: Number(row.multiplicador),
        });
      } catch {
        /* sin cupón → precio full */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Captura de atribución al montar: quien cae DIRECTO en esta pantalla con
  // ?utm_*/?ref= y cookies de Meta no pasa por /auth, así que capturamos acá
  // (ADR-613). Baja todo a localStorage; se persiste tras el signUp.
  useEffect(() => {
    captureAttributionParams();
  }, []);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setAuthLoading(true);
    try {
      if (authMode === 'signup') {
        const { data, error: signUpErr } = await supabase.auth.signUp({
          email: email.trim(),
          password,
        });
        if (signUpErr) throw signUpErr;
        if (!data.session) {
          // La cuenta ATLAS no pide confirmar mail, pero por las dudas.
          throw new Error('Revisá tu correo para confirmar la cuenta y volvé a entrar.');
        }
        // Atribución de marketing (UTM + Meta + ref) — mismo guardado que el
        // alta por /auth; esta pantalla no lo hacía y perdía la atribución
        // (ADR-613). Best-effort: nunca lanza ni bloquea el flujo de pago.
        await saveSignupAttribution(data.session.user.id);
        setStep('datos');
        // Registro exitoso (solo signup, NO signin) → evento 'signup' del embudo.
        void trackEvent('signup');
      } else {
        const { data: signInData, error: signInErr } =
          await supabase.auth.signInWithPassword({
            email: email.trim(),
            password,
          });
        if (signInErr) throw signInErr;
        if (!signInData.user) {
          throw new Error('No se pudo obtener la sesión. Reintentá.');
        }
        const next = await resolveStepForUser(signInData.user.id);
        if (next === 'redirect') {
          navigate(POST_PAY_DEST, { replace: true });
          return;
        }
        setStep(next === 'error' ? 'error-estado' : next);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo continuar. Reintentá.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleDatos = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const industry =
      industryOption === 'Otro' ? customIndustry.trim() : industryOption;
    if (!name.trim()) return setError('El nombre es obligatorio');
    if (!pais) return setError('Seleccioná un país');
    if (!companyName.trim()) return setError('El nombre de la marca es obligatorio');
    if (!industry) return setError('El rubro es obligatorio');
    setDatosLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Sesión no encontrada. Volvé a iniciar sesión.');
      const res = await createCompanyForUser({
        userId: user.id,
        name,
        pais,
        companyName,
        industry,
        // Cierra el linkeo empresa ⟷ sesión anónima (el `sid` que mandó la landing).
        sessionId: new URLSearchParams(window.location.search).get('sid'),
      });
      if (!res.ok) throw new Error(res.error || 'No se pudo crear la empresa.');
      setStep('pago');
      // El evento 'conversion' NO se emite acá: se emite al ENTRAR al paso de
      // pago, mirando el estado. Ver el effect de arriba y ADR-594.
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear la cuenta.');
    } finally {
      setDatosLoading(false);
    }
  };

  // ── Resolviendo un slug que no es del catálogo (puede ser un plan a medida) ──
  if (planCustomEstado === 'cargando') {
    return (
      <div className="min-h-screen bg-surface-bg flex items-center justify-center p-6">
        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  // ── El plan existe pero no se pudo leer su precio (Stripe/server caídos) ──
  // Mensaje distinto al de link inválido a propósito: el link ES válido y reintentar sirve.
  // Decirle "no existe" a un cliente que tiene el link correcto lo manda a molestarnos por
  // algo que se arregla solo en dos minutos.
  if (planCustomEstado === 'error') {
    return (
      <div className="min-h-screen bg-surface-bg flex items-center justify-center p-6 text-center">
        <div className="max-w-md">
          <h1 className="text-[22px] font-semibold text-brand-black/90 mb-2">
            No pudimos cargar tu plan
          </h1>
          <p className="text-[15px] text-neutral-500">
            Fue un problema momentáneo de nuestro lado. Actualizá la página en unos minutos; si
            sigue igual, escribinos.
          </p>
        </div>
      </div>
    );
  }

  // ── Plan inválido (slug malo, y tampoco es un plan a medida) ──
  if (!planCatalogo && !planCustom) {
    return (
      <div className="min-h-screen bg-surface-bg flex items-center justify-center p-6 text-center">
        <div className="max-w-md">
          <h1 className="text-[22px] font-semibold text-brand-black/90 mb-2">
            Link no válido
          </h1>
          <p className="text-[15px] text-neutral-500">
            Este link de alta no corresponde a un plan disponible. Pedile a tu
            contacto en Atlas el link correcto.
          </p>
        </div>
      </div>
    );
  }

  // ── Vista unificada del plan (catálogo público o a medida) ──
  // De acá para abajo el render no distingue: usa `planNombre`, `planFeatures` y `priceLabel`.
  const esCustom = !planCatalogo;
  // Los planes del catálogo se llaman "Ultra Nivel 1" y el copy pone el "Plan" adelante. Un
  // plan a medida puede venir nombrado "Plan de Prueba", que daba "Suscribite al Plan Plan de
  // Prueba" → se le saca el prefijo para los copys que ya dicen "plan".
  const planNombre = planCatalogo
    ? planCatalogo.name
    : nombrePlanSinPrefijo(planCustom!.nombre);
  // Los planes a medida tienen su propia lista (ver featuresPlanCustom): la cuota negociada en
  // créditos primero, porque imágenes y videos salen del mismo pozo.
  const planFeatures = planCatalogo
    ? planCatalogo.features
    : featuresPlanCustom(planCustom!.maxFotosMes);
  // Lo que se le manda a /stripe/checkout: id del catálogo o slug del plan a medida.
  const planParaCheckout = planCatalogo ? planId! : planCustom!.slug;

  // `planCatalogo.price` es el precio en ARS; para el resto de los países se resuelve
  // con la misma tabla que usa el checkout normal (módulo compartido). Los planes a medida
  // NO pasan por ahí: su precio ya viene de Stripe en su moneda final.
  const price = planCatalogo
    ? getPlanPriceForPais(planCatalogo.id, planCatalogo.price, pais)
    : 0;
  // Los planes a medida siempre cobran por Stripe, sin importar el país que elija el cliente:
  // son para el exterior por definición, y Rebill ni siquiera tiene el plan cargado.
  const esArgentina = !esCustom && paisFlags(pais).isArgentina;
  // Guard `> 0` además de `< 1`: si la DB tuviera `multiplicador = 0` (dato malo),
  // sin el guard `Math.floor(price*0)` mostraría "$0"; con el guard cae a precio
  // full. `Math.round` en vez de `floor` para redondear parejo.
  // Planes que NO admiten el cupón "Hot Sale". El Básico quedó afuera por
  // decisión comercial: su precio ya es el de entrada y no lleva promoción.
  //
  // Esto YA estaba bien en `Checkout.tsx`, que exige
  // `id === 'negocio' || isEnterprisePlan(id)`. Acá faltaba el filtro, así que
  // los links directos de la landing (`/alta-empresa/basico`) aplicaban el 30%
  // a un plan que no lo tiene. Y no era solo visual: el `couponCode` viaja a
  // `RebillCardPayment` y el SDK lo auto-aplica, o sea que se cobraba de menos.
  //
  // Se expresa como lista negra y no como lista blanca a propósito: si mañana
  // se agrega un plan nuevo, lo razonable es que herede la promo, no que quede
  // silenciosamente afuera.
  const PLANES_SIN_CUPON = ['emprendimiento'];
  // Además del plan, el cupón exige ARGENTINA: el "Hot Sale" es una promo
  // local y la landing solo muestra precio tachado cuando el país es AR
  // (`originalPrice: countryCode === "AR" ? … : null`). Sin este guard, un
  // chileno vería "el primer mes" con un descuento que su cobro por Stripe no
  // aplica — o sea, exactamente el engaño que estamos sacando. Ver ADR-592.
  //
  // Los planes a medida tampoco admiten cupón, por una razón propia: su precio YA es el
  // negociado con ese cliente. Encimarle la promo general sería un descuento sobre un
  // descuento que nadie acordó. El payment server lo bloquea también del lado del server, así
  // que no depende de que el front se porte bien.
  const planAdmiteCupon =
    !esCustom &&
    !PLANES_SIN_CUPON.includes(planCatalogo!.id) &&
    paisFlags(pais).isArgentina;

  const mult =
    planAdmiteCupon &&
    coupon &&
    coupon.multiplicador > 0 &&
    coupon.multiplicador < 1
      ? coupon.multiplicador
      : null;
  const hasDiscount = mult !== null;
  const bigPrice = mult !== null ? Math.round(price * mult) : price;
  // Precio a medida: viene de Stripe ya en su moneda, así que NO pasa por la conversión por
  // país (`formatPriceForPais` asume un monto en ARS y lo convierte).
  const priceLabel = esCustom
    ? formatPrecioCustom(planCustom!.precio, planCustom!.moneda)
    : formatPriceForPais(bigPrice, pais); // grande (con descuento si hay)
  const fullPriceLabel = esCustom
    ? priceLabel
    : formatPriceForPais(price, pais); // full ("Después … / mes")

  // ── Pago del exterior: Stripe por redirect ───────────────────────────
  // Argentina cobra con el checkout embebido de Rebill (in-app). El resto del
  // mundo va a Stripe, que es un REDIRECT: se le pide la URL al payment server
  // y se manda al usuario para afuera. Es el mismo endpoint y el mismo shape
  // que usa `Checkout.tsx` para los no-argentinos; acá no se reinventa nada.
  //
  // OJO con la consecuencia de UX: al ser redirect, el usuario SE VA de esta
  // página, así que la pantalla de éxito de este flujo (`step === 'exito'`) no
  // se muestra para el exterior. Vuelve adonde esté configurado el
  // `success_url` de la Checkout Session.
  const irAStripe = async () => {
    if (stripeLoading) return;
    setStripeLoading(true);
    setError(null);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Sesión no encontrada. Volvé a iniciar sesión.');

      const url = await fetchStripeCheckoutUrl({
        user_id: user.id,
        // Id del catálogo ('enterprise2') o slug del plan a medida ('zara'): el payment
        // server resuelve los dos y saca el price que corresponda.
        plan: planParaCheckout,
        // SIN cupón, por dos razones. Primero: el Hot Sale es una promo
        // argentina y este camino es justamente el del exterior, así que
        // no corresponde. Segundo, aunque correspondiera, no se podría
        // mandar bien: el RPC `get_alta_empresa_coupon` devuelve solo
        // `(codigo, multiplicador)` y el endpoint espera `cupon_id`.
        // Mandar `con_descuento: true` sin el id sería un payload a medio
        // armar contra un endpoint que COBRA. Antes de eso, precio full.
      });
      // Se creó la Checkout Session de Stripe → estamos por redirigir. Es el último
      // evento que el app puede emitir para el exterior (después el usuario se va a
      // Stripe): separa "vio el botón" (checkout_view) de "llegó a crear el pago".
      // Lo ESPERAMOS antes de redirigir: el redirect cross-origin cancela el
      // insert en vuelo, así que fire-and-forget lo perdería casi siempre. Con
      // tope de tiempo para no bloquear el pago si la DB está lenta/caída — el
      // pago manda, el tracking es best-effort.
      await Promise.race([
        trackEvent('checkout_created'),
        new Promise((resolve) => setTimeout(resolve, 1500)),
      ]);
      window.location.href = url;
    } catch (err: any) {
      setError(err?.message || 'No se pudo iniciar el pago.');
      setStripeLoading(false);
    }
  };

  // ── Resumen del plan (columna izquierda / cabecera mobile) ──
  // OJO: son funciones que se LLAMAN ({renderX()}), no componentes (<X />).
  // Definirlos como componentes acá adentro haría que React los re-monte en
  // cada render (nueva identidad de función) → el input pierde el foco al tipear.
  const renderPlanSummary = () => (
    <>
      <div className="max-w-[440px]">
        <p className="text-[15px] text-neutral-500 mb-1">
          Suscribite al Plan {planNombre}
        </p>
        <div className="flex items-baseline gap-2.5">
          <span className="text-[46px] leading-none font-bold text-brand-black/90 tracking-tight">
            {priceLabel}
          </span>
          <span className="text-[14px] text-neutral-500">
            {hasDiscount ? 'el primer mes' : 'por mes'}
          </span>
        </div>
        {hasDiscount && (
          <p className="text-[13px] text-neutral-600 mt-2">
            Después {fullPriceLabel} / mes
          </p>
        )}
        <ul className="mt-10 space-y-3">
          {planFeatures.map((feature, i) => (
            <li
              key={i}
              className="flex items-center text-[14px] text-neutral-600"
            >
              <CheckCircle2 className="w-4 h-4 text-brand-primary mr-2.5 flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>
      </div>
      <p className="mt-auto pt-10 text-[12px] text-neutral-400">
        Se renueva automáticamente cada mes. Cancelás cuando quieras.
      </p>
    </>
  );

  // ── Contenido del paso actual (columna derecha) ──
  const renderStepContent = () => {
    if (step === 'loading') {
      return (
        <div className="flex items-center justify-center gap-2 py-10 text-[15px] text-neutral-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </div>
      );
    }

    // No se pudo leer el estado de la cuenta → no se cobra. Mismo criterio (y
    // casi el mismo texto) que la puerta de `Checkout.tsx`: sin saber si ya hay
    // una suscripción activa, seguir sería arriesgarse a duplicarla.
    if (step === 'error-estado') {
      return (
        <div className="py-10 text-center">
          <p className="text-[15px] text-brand-black/90">
            No pudimos verificar el estado de tu cuenta, así que no seguimos con
            el cobro para no duplicar una suscripción.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 inline-flex h-10 items-center justify-center rounded-md bg-brand-primary px-4 text-[15px] font-medium text-white shadow-sm transition-colors hover:bg-brand-primary-hover"
          >
            Reintentar
          </button>
        </div>
      );
    }

    if (step === 'exito') {
      return (
        <div>
          <h2 className="text-[24px] font-bold text-brand-black/90 tracking-tight mb-2">
            ¡Listo! Tu plan está activo
          </h2>
          <p className="text-[15px] text-neutral-500 mb-8">
            Ya podés empezar a usar Atlas con todo tu plan {planNombre}.
          </p>
          <button
            type="button"
            onClick={() => navigate(POST_PAY_DEST, { replace: true })}
            className="inline-flex items-center justify-center py-3 px-6 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
          >
            Empezar a usar Atlas
          </button>
        </div>
      );
    }

    if (step === 'pago') {
      // Argentina → Rebill embebido (in-app). Resto → Stripe (redirect).
      // Misma regla que `Checkout.tsx`; antes esta pantalla montaba Rebill para
      // todo el mundo y le mostraba precios en ARS a un chileno. Ver ADR-592.
      if (!esArgentina) {
        return (
          <div className="space-y-4">
            <p className="text-[14.5px] text-[#3C3C43]/70 leading-snug">
              Te vamos a llevar a Stripe para completar tu suscripción de forma
              segura. Volvés a Atlas apenas termines.
            </p>
            <button
              type="button"
              onClick={irAStripe}
              disabled={stripeLoading}
              className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-50 text-white text-[15px] font-semibold shadow-sm transition-colors"
            >
              {stripeLoading ? 'Redirigiendo…' : 'Continuar al pago'}
            </button>
          </div>
        );
      }
      // Sin título extra: va directo al form de Rebill, igual que el checkout.
      return (
        <RebillCardPayment
          // Solo se llega acá con esArgentina, que exige !esCustom → planCatalogo existe.
          // Rebill no conoce los planes a medida (son exterior/Stripe por definición).
          planId={planCatalogo!.id}
          // Emprendimiento (`/alta-empresa/basico`) está en PLANES_SIN_CUPON, así
          // que acá viaja en `false` y el componente exige precio de lista,
          // limpiando cualquier descuento arrastrado de otro plan en la misma
          // página (ADR-2026-08-05-cupon-se-arrastra-entre-planes).
          planAdmiteCupon={planAdmiteCupon}
          couponCode={hasDiscount ? (coupon?.codigo ?? null) : null}
          onSuccess={(_r: RebillCheckoutResult) => {
            // `pago_ok` = pagó DE VERDAD, a diferencia de `conversion`, que
            // solo dice que llegó al formulario. Ver ADR-593.
            //
            // Solo cubre Argentina (Rebill es in-app, así que el éxito ocurre
            // acá). El camino Stripe es un REDIRECT: el usuario se va de la
            // página y este código nunca corre. Para cubrirlo hay que emitir el
            // evento desde el workflow `stripe webhook` de n8n, en
            // `invoice.payment_succeeded`. Queda pendiente y anotado.
            void trackEvent('pago_ok');
            setStep('exito');
          }}
          onError={(msg) => setError(msg)}
        />
      );
    }

    if (step === 'datos') {
      return (
        <form onSubmit={handleDatos} className="space-y-6">
          <div>
            <h2 className="text-[22px] font-semibold text-brand-black/90 mb-1">
              Contanos de tu marca
            </h2>
            <p className="text-[14px] text-neutral-500">
              Con estos datos armamos tu cuenta.
            </p>
          </div>
          <div>
            <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
              Nombre y apellido
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
              placeholder="Tu nombre"
              required
            />
          </div>
          <div>
            <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
              País
            </label>
            <CountrySelect value={pais} onChange={setPais} required />
          </div>
          <div>
            <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
              Nombre de la marca
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
              placeholder="Nombre de tu marca o negocio"
              required
            />
          </div>
          <div>
            <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
              Rubro
            </label>
            <div className="flex flex-col md:flex-row gap-3">
              <div className={industryOption === 'Otro' ? 'md:w-1/2' : 'w-full'}>
                <RubroSelect value={industryOption} onChange={setIndustryOption} />
              </div>
              {industryOption === 'Otro' && (
                <input
                  type="text"
                  value={customIndustry}
                  onChange={(e) => setCustomIndustry(e.target.value)}
                  placeholder="Especificá tu rubro"
                  className="w-full md:w-1/2 px-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
                  required
                />
              )}
            </div>
          </div>
          <button
            type="submit"
            disabled={datosLoading}
            className="w-full h-[var(--btn-h)] px-8 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[15px] shadow-sm transition-colors disabled:opacity-50"
          >
            {datosLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Creando tu cuenta…
              </>
            ) : (
              'Continuar'
            )}
          </button>
        </form>
      );
    }

    // step === 'auth'
    return (
      <form onSubmit={handleAuth} className="space-y-5">
        <div>
          <h2 className="text-[22px] font-semibold text-brand-black/90 mb-1">
            {authMode === 'signup' ? 'Creá tu cuenta' : 'Iniciá sesión'}
          </h2>
          <p className="text-[14px] text-neutral-500">
            {authMode === 'signup'
              ? 'Empezá tu suscripción al plan ' + planNombre + '.'
              : 'Entrá con tu cuenta para continuar.'}
          </p>
        </div>
        <div>
          <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
            Email
          </label>
          <div className="relative">
            <Mail className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
              placeholder="tu@email.com"
              required
            />
          </div>
        </div>
        <div>
          <label className="text-[13px] font-medium text-neutral-600 mb-1 block">
            Contraseña
          </label>
          <div className="relative">
            <Lock className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary outline-none transition-colors bg-surface-card"
              placeholder="••••••••"
              minLength={6}
              required
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={authLoading}
          className="w-full h-[var(--btn-h)] px-8 inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-md font-medium text-[15px] shadow-sm transition-colors disabled:opacity-50"
        >
          {authLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Un momento…
            </>
          ) : authMode === 'signup' ? (
            'Crear cuenta'
          ) : (
            'Iniciar sesión'
          )}
        </button>
        <p className="text-[13px] text-neutral-500 text-center">
          {authMode === 'signup' ? '¿Ya tenés cuenta? ' : '¿No tenés cuenta? '}
          <button
            type="button"
            onClick={() => {
              setAuthMode(authMode === 'signup' ? 'signin' : 'signup');
              setError(null);
            }}
            className="text-brand-primary font-medium hover:underline"
          >
            {authMode === 'signup' ? 'Iniciá sesión' : 'Creá una'}
          </button>
        </p>
      </form>
    );
  };

  const renderLogo = (size: 'sm' | 'md' = 'md') => (
    <div className="flex items-center gap-2.5">
      <div
        className={`${size === 'sm' ? 'w-7 h-7' : 'w-9 h-9'} rounded-full bg-brand-black flex items-center justify-center shrink-0`}
      >
        <img
          src="/images/estudio-atlas-logo-solo-blanco.svg"
          alt=""
          className={size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'}
        />
      </div>
      <span
        className={`${size === 'sm' ? 'text-[14px]' : 'text-[16px]'} font-semibold text-brand-black/90`}
      >
        Estudio Atlas
      </span>
    </div>
  );

  const renderErrorBanner = () =>
    error ? (
      <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        <span>{error}</span>
      </div>
    ) : null;

  return (
    <div
      // En desktop la página es su propio contenedor de scroll con el gutter de
      // la scrollbar RESERVADO (scrollbar-gutter: stable): así, abrir el dropdown
      // de rubro (que se pasa del viewport) NO hace aparecer/desaparecer la barra
      // del body ni corre el layout a la izquierda. Fondo blanco en desktop → el
      // gutter reservado (extremo derecho) queda invisible.
      className="min-h-screen md:h-screen md:overflow-y-auto bg-surface-card"
      style={{ scrollbarGutter: 'stable' }}
    >
      {/* ═══════════ MOBILE (homogéneo con /checkout) ═══════════ */}
      <div className="md:hidden flex flex-col min-h-screen pb-10">
        {/* Header: logo + "Pago seguro" (igual que el checkout). */}
        <div
          className="px-5 pb-2 flex items-center justify-between"
          style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1.25rem)' }}
        >
          {renderLogo('sm')}
          <div className="flex items-center gap-1 text-[11px] text-neutral-400">
            <Lock className="w-3 h-3" />
            <span>Pago seguro</span>
          </div>
        </div>

        {/* Resumen del plan compacto y centrado + "Ver qué incluye" colapsable
            (features ocultas por defecto), igual que el checkout. */}
        <div className="px-5 pt-7 pb-5 border-b border-neutral-200 text-center">
          <p className="text-[13px] text-neutral-500">Plan {planNombre}</p>
          <p className="text-[28px] font-bold text-brand-black/90 tracking-tight leading-tight mt-1">
            {priceLabel}
            <span className="text-[13px] font-normal text-neutral-400 ml-1">
              {hasDiscount ? 'el primer mes' : '/ mes'}
            </span>
          </p>
          {hasDiscount && (
            <p className="text-[13px] text-neutral-600 mt-0.5">
              Después {fullPriceLabel} por mes
            </p>
          )}
          <button
            type="button"
            onClick={() => setShowFeatures(!showFeatures)}
            className="mt-3 inline-flex items-center gap-1 text-[13px] text-brand-primary hover:text-brand-primary-hover font-medium transition-colors"
          >
            Ver qué incluye
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${showFeatures ? 'rotate-180' : ''}`}
            />
          </button>
          {showFeatures && (
            <ul className="mt-3 space-y-2 text-left">
              {planFeatures.map((feature, i) => (
                <li
                  key={i}
                  className="flex items-center text-[13px] text-neutral-600"
                >
                  <CheckCircle2 className="w-3.5 h-3.5 text-brand-primary mr-2 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Contenido del paso (auth / datos / pago). */}
        <div className="px-5 pt-5">
          {renderErrorBanner()}
          {renderStepContent()}
        </div>
      </div>

      {/* ═══════════ DESKTOP ═══════════ */}
      <div className="hidden md:flex min-h-screen">
        {/* Izquierda — resumen del plan */}
        <div className="w-1/2 bg-[#DDDFE6] flex flex-col px-10 lg:px-20 py-12">
          <div className="mb-16">
            {renderLogo()}
          </div>
          {renderPlanSummary()}
        </div>
        {/* Derecha — el paso actual */}
        <div className="w-1/2 bg-surface-card flex flex-col justify-center px-10 lg:px-20 py-12">
          <div className="w-full mx-auto max-w-[480px]">
            {renderErrorBanner()}
            {renderStepContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseCheckout;
